// ParticleCanvas.js
// Pure canvas — no external deps.
// 30 dust particles floating upward in the arena.

import { useEffect, useRef } from 'react';

const PARTICLE_COUNT = 30;

function rand(min, max) { return Math.random() * (max - min) + min; }

function makeParticle(w, h) {
  return {
    x:     rand(0, w),
    y:     rand(0, h),
    r:     rand(0.4, 1.8),
    speed: rand(0.08, 0.35),
    drift: rand(-0.08, 0.08),
    alpha: rand(0.02, 0.1),
    hue:   Math.random() > 0.6 ? '50,245,210' : Math.random() > 0.5 ? '139,108,255' : '246,247,251',
  };
}

export default function ParticleCanvas() {
  const canvasRef = useRef(null);
  const stateRef  = useRef({ particles: [], raf: null });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Init particles
    stateRef.current.particles = Array.from(
      { length: PARTICLE_COUNT },
      () => makeParticle(canvas.width, canvas.height)
    );

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const { particles } = stateRef.current;

      particles.forEach(p => {
        // Draw
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.hue}, ${p.alpha})`;
        ctx.fill();

        // Move
        p.y    -= p.speed;
        p.x    += p.drift;
        p.alpha = p.alpha * 0.999 + rand(-0.001, 0.001);

        // Reset when off screen
        if (p.y < -4) {
          p.y = canvas.height + 4;
          p.x = rand(0, canvas.width);
        }
      });

      stateRef.current.raf = requestAnimationFrame(tick);
    };

    stateRef.current.raf = requestAnimationFrame(tick);

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      cancelAnimationFrame(stateRef.current.raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="au-particle-canvas"
      aria-hidden="true"
    />
  );
}